import React, { useEffect, useState } from 'react'
import OffConvas from '../../components/OffConvas';
import IdCardForm from './components/IdCardForm';
import { idCardService } from "./services/idCardService";
import "../../utils/extensions";
import { statusClassMap } from "../../utils/helpers";
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import { toast } from '../../services/toastService';
import Pagination from '../../components/Pagination';
import Loading from '../../components/Loading';
import { useAuth } from '../../hooks/useAuth';

const IdCards = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("Admin");
  const [data, setData] = useState([]);
  const [id,setId] = useState(0);
  const [deleteId, setDeleteId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(()=>{
      loadData();
  },[])
  const loadData = async (pageNumber=1) => {
   try {
    setLoading(true);
     const res = await idCardService.getAll({
      pageNumber: pageNumber,
      pageSize: 15,
      orderBy: "id",
      orderDirection: "desc",
    });
    setData(res.value);
   } catch (error) {
    console.log(error);
   }
   finally{
    setLoading(false);
   }
  };
  const deleteAsync =async ()=>{
    try {
      setLoading(true);
      if(!deleteId) {
        toast.error("Please select a record to delete");
        return;
      }
      var result = await idCardService.delete(deleteId);
      if(!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to delete record");
        return;
      }
      toast.success("Record deleted successfully");
      setDeleteId(0);
      if(!search)
      loadData(data.pageNumber);
    else
      getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while deleting the record");
    }
    finally{
      setLoading(false);
    }
  }
  const getAllSearchAsync = async (pageNumber=1,signal) => {
    try {
      var res = await idCardService.getAllSearchAsync({
      search:search,
      pageNumber:pageNumber,
      pageSize: 15,
      orderBy: "id",
      orderDirection: "desc",
    },signal);
     setData(res.value);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  // 🟢 Search effect with debounce & cancel
  useEffect(() => {
    if (search.trim().length < 2) {
      loadData(1); // reset to default if search is cleared
      return;
    }

    const controller = new AbortController(); // cancel token
    const delay = setTimeout(() => {
      getAllSearchAsync(1,controller.signal);
    }, 400); // debounce time

    return () => {
      controller.abort(); // cancel previous request
      clearTimeout(delay); // clear debounce
    };
  }, [search]);

  const onPageChange = async (pageNumber) => {
    setLoading(true);
    if (!search) {
      loadData(pageNumber);
      return;
    }
    await getAllSearchAsync(pageNumber);
  };

  const togglePublishAsync = async (record) => {
    try {
      setLoading(true);
      const result = await idCardService.changePublishStatus(record.id, !record.isPublished);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update publish status");
        return;
      }
      toast.success("Publish status updated successfully");
      if (!search) loadData(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while updating the publish status");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
    {loading && <Loading />}
      <section className="section-documents">
        <div className="my-card">
          <div className="my-card-header">
            <div className="my-card-title">Id Cards</div>
            <button
              type="button"
              className="my-button my-button-dark"
              data-bs-toggle="offcanvas"
              data-bs-target="#my-id-card"
            >
              Add New
            </button>
          </div>
          <div className="my-card-body">
            <div className="my-grid">
              <div className="my-grid-header">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="form-control my-grid-search"
                />
              </div>
              <div className="my-grid-body">
                <table className="table table-bordered table-responsive">
                  <thead>
                    <tr>
                      <th>Card No.</th>
                      <th>Iqama No.</th>
                      {/* Label changed from "Operator Name" to "Name" per client-side requirement (admin UI text only) */}
                      <th>Name</th>
                      <th>Company Name</th>
                      <th>Issue Date</th>
                      <th>Expiry Date</th>
                      <th>Status</th>
                      <th>Published</th>
                      {isAdmin && <th className="text-center">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((record) => (
                      <tr key={record.id}>
                        <td>{record.cardNo}</td>
                        <td>{record.iqamaNumber}</td>
                        <td>{record.operatorName}</td>
                        <td>{record.companyName}</td>
                        <td>{record.issueDate?.toDisplayDate()}</td>
                        <td>{record.expiryDate?.toDisplayDate()}</td>
                        <td>
                          <span
                            className={`badge ${statusClassMap[record.expiryDate?.getExpiryStatus()]}`}
                          >
                            {record.expiryDate?.getExpiryStatus()}
                          </span>
                        </td>
                        <td>
                          {isAdmin ? (
                            <div className="form-check form-switch">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                role="switch"
                                checked={!!record?.isPublished}
                                onChange={() => togglePublishAsync(record)}
                              />
                            </div>
                          ) : (
                            <span className={`badge ${record?.isPublished ? "bg-success" : "bg-secondary"}`}>
                              {record?.isPublished ? "On" : "Off"}
                            </span>
                          )}
                        </td>
                        {isAdmin && (
                          <td className="my-grid-actions">
                            <i
                              className="fas fa-pencil text-primary cursor-pointer"
                              data-bs-toggle="offcanvas"
                              data-bs-target="#my-id-card"
                              onClick={() => setId(record.id)}
                            />
                            <i
                              className="fas fa-trash text-danger cursor-pointer"
                              data-bs-toggle="modal"
                              data-bs-target="#confirm-modal"
                              onClick={() => setDeleteId(record.id)}
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="my-grid-footer">
                <Pagination currentPage={data.pageNumber} totalPages={data.totalPages} onPageChange={onPageChange} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <DeleteConfirmModal deleteAsync={deleteAsync} />
      <OffConvas title="Id Card" id="my-id-card">
        <IdCardForm loadData={loadData} id={id} setId={setId} />
      </OffConvas>
    </>
  );
};

export default IdCards